import type { ChartImage, DropdownOption, LivePlotImageOptions, LivePlotSizeOptions, PlotSpec, PrintChartAspect, RichSummary } from '../shared';
import type { ParsedValues, SecondaryFragmentsConstants } from './validation';

export const APP_NAME = 'iSecondaryFragments';
export const APP_VERSION = '2.2.0';
export const APP_VERSION_POLICY = {
  currentVersion: APP_VERSION,
  rerunRequiredBefore: APP_VERSION,
  message: 'Figure 2-250 now uses the GRF chart ordinate. Re-run this calculation before relying on its velocity or range.'
};

export const API_PATH = '/iSecondaryFragments/';
export const CONSTANTS_API_PATH = '/iSecondaryFragments/constants/';

export type ChartKey = '02_248' | '02_250' | '02_251' | '02_252' | '02_007';

export const LIVE_CHART_ORDER: ChartKey[] = ['02_007', '02_248', '02_250', '02_251', '02_252'];

export const PRINT_CHART_ORDER: ChartKey[] = ['02_007', '02_248', '02_250', '02_251', '02_252'];

export const PRINT_CHART_ASPECT = {
  '02_248': 'square',
  '02_250': 'square',
  '02_251': 'square',
  '02_252': 'square',
  '02_007': 'square'
} satisfies Record<ChartKey, PrintChartAspect>;

export const PRINT_CHART_IMAGE_OPTIONS: LivePlotImageOptions = {
  width: 600,
  height: 600,
  scale: 2,
  layout: {
    font: { size: 15 },
    title: { font: { size: 20 } },
    margin: { l: 88, r: 28, t: 78, b: 114 },
    xaxis: { title: { font: { size: 17 } }, tickfont: { size: 13 } },
    yaxis: { title: { font: { size: 17 } }, tickfont: { size: 13 } },
    legend: { font: { size: 13 }, x: 0.5, xanchor: 'center', y: -0.32 }
  }
};

export const LIVE_PLOT_CARD_MIN_HEIGHT = 'clamp(20rem, 72vw, 34rem)';

const SQUARE_LIVE_PLOT_SIZE = {
  fit: 'width',
  width: '100%',
  maxWidth: '42em',
  aspectRatio: '1 / 1',
  titleFontSize: 12,
  axisTitleFontSize: 12,
  tickFontSize: 10
} satisfies LivePlotSizeOptions;

export const LIVE_PLOT_SIZE_BY_KEY = {
  '02_248': SQUARE_LIVE_PLOT_SIZE,
  '02_250': SQUARE_LIVE_PLOT_SIZE,
  '02_251': SQUARE_LIVE_PLOT_SIZE,
  '02_252': SQUARE_LIVE_PLOT_SIZE,
  '02_007': SQUARE_LIVE_PLOT_SIZE
} satisfies Record<ChartKey, LivePlotSizeOptions>;

const DJANGO_STATIC_PREVIEW_ROOT = '/static/images/secondary_fragments';

export const YES_NO_OPTIONS = [
  { value: 'No', label: 'No' },
  { value: 'Yes', label: 'Yes' }
] satisfies DropdownOption[];

export const FRAGMENT_PREVIEW_BY_SHAPE: Record<string, string> = {
  'Circular Cylinder (Long Rod), Side On': `${DJANGO_STATIC_PREVIEW_ROOT}/t281.png`,
  Sphere: `${DJANGO_STATIC_PREVIEW_ROOT}/t282.png`,
  'Rod , End-On': `${DJANGO_STATIC_PREVIEW_ROOT}/t283.png`,
  'Disc, Face-On': `${DJANGO_STATIC_PREVIEW_ROOT}/t284.png`,
  'Cube, Face-On': `${DJANGO_STATIC_PREVIEW_ROOT}/t285.png`,
  'Cube, Edge-On': `${DJANGO_STATIC_PREVIEW_ROOT}/t286.png`,
  'Long Rectangular Member, Face-On': `${DJANGO_STATIC_PREVIEW_ROOT}/t287.png`,
  'Long Rectangular Member, Edge-On': `${DJANGO_STATIC_PREVIEW_ROOT}/t288.png`,
  'Narrow Strip, Face-On': `${DJANGO_STATIC_PREVIEW_ROOT}/t289.png`
};

export interface APIPayload {
  chargeShape: string;
  weaponSize: string;
  standoff: string;
  cylinderLength?: string;
  targetShape: string;
  targetLocation: string;
  targetLength: string;
  targetHalfWidth: string;
  targetWeight: string;
  constrained: boolean;
  materialType?: string;
  boundaryCondition?: string;
}

export interface APIResponse {
  summary: RichSummary;
  plotData: Partial<Record<ChartKey, PlotSpec>>;
}

export type SecondaryFragmentsResultState = APIResponse & {
  charts?: Partial<Record<ChartKey, ChartImage>>;
};

export function dropdownOptions(values: readonly string[]): DropdownOption[] {
  return values.map(value => ({ value, label: value }));
}

export function preferredChoice(values: readonly string[], preferred: string): string {
  return values.includes(preferred) ? preferred : values[0];
}

export function buildInitialChoices(constants: SecondaryFragmentsConstants): Pick<ParsedValues, 'chargeShape' | 'targetShape' | 'targetLocation' | 'materialType' | 'boundaryCondition'> {
  return {
    chargeShape: preferredChoice(constants.chargeShape, 'Sphere'),
    targetShape: preferredChoice(constants.targetShape, 'Circular Cylinder (Long Rod), Side On'),
    targetLocation: preferredChoice(constants.targetLocation, 'On the surface'),
    materialType: preferredChoice(constants.materialType, 'ASTM A 36'),
    boundaryCondition: preferredChoice(constants.boundaryCondition, 'Cantilever')
  };
}
